import { useState, FC, useEffect } from "react";
import {
  CollectionBookContext,
  CollectionBookContextValue,
  CollectionBookProviderProps,
} from ".";
import { ErrorResponse } from "../../../Models/Error";
import { Collection } from "../../../Models/Collection";
import { CollectionsService } from "../../../services/shipment/collections";
import { callErrorToast } from "../../../utilities/utilities";

/* ===========================================================================
 * SERVICE INSTANTIATION
 * =========================================================================== */
const collectionsService = new CollectionsService();

/* ===========================================================================
 * PROVIDER: CollectionBookProvider
 * =========================================================================== */

/**
 * @component CollectionBookProvider
 *
 * Provides context for booking multiple collections simultaneously.
 * It manages the loading state, error handling, and batch execution
 * of booking requests using Promise.allSettled to handle partial successes.
 *
 * @param {CollectionBookProviderProps} props - Provider properties including data and optional callback.
 * @returns {JSX.Element} A React Context Provider for collection booking operations.
 */
export const CollectionBookProvider: FC<CollectionBookProviderProps> = ({
  children,
  data = [],
  callback,
}) => {
  /** Indicates if a batch booking operation is in progress. */
  const [loader, setLoader] = useState<boolean>(false);

  /** Stores the error response if one or more bookings fail. */
  const [error, setError] = useState<ErrorResponse<Collection> | null>(null);

  /* ---------------------------------------------------------------------------
   * FUNCTION: bookCollections
   * Handles the asynchronous batch booking process.
   * ------------------------------------------------------------------------- */

  /**
   * Iterates through the provided collection IDs and attempts to book them.
   * Uses Promise.allSettled to ensure all requests complete regardless of failure.
   *
   * @async
   * @returns {Promise<void>}
   */
  const bookCollections = async (): Promise<void> => {
    setLoader(true);

    try {
      // Execute all booking requests in parallel
      const results = await Promise.allSettled(
        data.map((id) => collectionsService.book(id))
      );

      // Separate results into successful and rejected categories
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled"
      );

      // If at least one booking failed
      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<Collection>;
        setError(firstError);

        // Even with partial failures, trigger callback if some succeeded
        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      // If all bookings were successful
      if (callback) callback();
    } catch (unexpected) {
      // Generic error fallback for unexpected exceptions
      const err: ErrorResponse<Collection> = {
        response: {
          data: {},
        },
      };

      setError(err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /**
   * Triggers a global error toast whenever an error state is set.
   */
  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  /* ---------------------------------------------------------------------------
   * CONTEXT VALUE & RENDER
   * ------------------------------------------------------------------------- */

  const contextValue: CollectionBookContextValue = {
    data,
    loader,
    bookCollections,
    callback,
  };

  return (
    <CollectionBookContext.Provider value={contextValue}>
      {children}
    </CollectionBookContext.Provider>
  );
};
